import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, Check, LockKeyhole, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { SmallCaps, BrassRule, PillCTA } from "../components/atelier/primitives";
import { AtelierModal } from "../components/atelier/AtelierModal";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { analytics } from "../lib/analytics";

const FOCUS = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-espresso";
const PREFERRED_PARTNERS = ["Atelier Di Falco", "Albuhara", "Macara Club", "Macara Travel"];
const CONCIERGE_TYPES = new Set(["concierge", "consultation", "styling", "priority_access", "invitation", "early_access"]);
const TIER_BENEFIT_KEYS = {
  "Falcon Society access": "falcon.tier_benefit.access",
  "Atelier partner benefit eligibility": "falcon.tier_benefit.partner_eligibility",
  "Private seasonal previews": "falcon.tier_benefit.private_previews",
  "Falcon Concierge priority": "falcon.tier_benefit.concierge_priority",
  "Invite-only privileges": "falcon.tier_benefit.invite_privileges",
  "Partner privilege upgrades": "falcon.tier_benefit.partner_upgrades",
  "Bespoke invitations": "falcon.tier_benefit.bespoke_invitations",
  "Highest-priority atelier handling": "falcon.tier_benefit.highest_priority",
};

const tierName = (code) => (code || "")
  .replace("FALCON_", "")
  .replaceAll("_", " ")
  .replace(/\b\w/g, (c) => c.toUpperCase());

const formatPoints = (value = 0) => new Intl.NumberFormat("az-AZ").format(Math.max(0, Number(value) || 0));

function tierLabel(t, tier) {
  if (!tier) return t("vip.tier.gentleman_member", "Centilmen üzv");
  return t(`vip.tier.${tier.code}`, tier.name || tierName(tier.code));
}

function tierBenefitLabel(t, benefit) {
  return t(TIER_BENEFIT_KEYS[benefit], benefit);
}

function benefitPartner(benefit, t) {
  return benefit?.partner?.name || t("falcon.partner.atelier", "Atelier Di Falco");
}

function benefitTypeLabel(benefit, t) {
  const type = String(benefit?.benefit_type || "").toLowerCase();
  const method = String(benefit?.redemption_method || "").toLowerCase();
  if (type === "voucher" || method.includes("voucher")) return t("falcon.type.voucher", "Voucher");
  if (CONCIERGE_TYPES.has(type) || method.includes("concierge")) return t("falcon.type.concierge", "Concierge vasitəsilə");
  return t("falcon.type.automatic", "Avtomatik");
}

function ctaForBenefit(benefit, t) {
  const type = benefitTypeLabel(benefit, t);
  if (!benefit.available) return t("falcon.cta.unlocks_at_level", "Bu səviyyədə açılır");
  if (type === t("falcon.type.concierge", "Concierge vasitəsilə")) return t("falcon.cta.create_concierge", "Concierge sorğusu yarat");
  if (type === t("falcon.type.voucher", "Voucher")) return t("falcon.cta.use", "İstifadə et");
  return t("falcon.cta.use", "İstifadə et");
}

function getErrorMessage(error, t) {
  return error?.response?.data?.detail || t("falcon.error.generic", "Məlumat yüklənmədi. Yenidən cəhd edin.");
}

export function FalconSocietyLanding() {
  const { user, ready } = useAuth();
  const { t } = useI18n();
  useDocumentTitle(t("falcon.doc_title", "Falcon Society"));
  const [tiers, setTiers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activePartner, setActivePartner] = useState("all");
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [actionBusy, setActionBusy] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const requests = [
        api.get("/falcon/tiers"),
        api.get("/falcon/partners"),
        api.get("/falcon/benefits"),
      ];
      if (user) requests.push(api.get("/falcon/dashboard"));
      const [tierResponse, partnerResponse, benefitResponse, dashboardResponse] = await Promise.all(requests);
      setTiers(tierResponse.data || []);
      setPartners(partnerResponse.data || []);
      setBenefits(benefitResponse.data || []);
      setDashboard(dashboardResponse?.data || null);
    } catch (err) {
      setError(getErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user?.id]);

  const membership = dashboard?.membership || null;
  const progress = dashboard?.progress || null;
  const currentTierCode = membership?.tier || null;
  const lifetimePoints = membership?.lifetime_points || 0;

  const partnerFilters = useMemo(() => {
    const names = new Set(partners.map((p) => p.name).filter(Boolean));
    const ordered = PREFERRED_PARTNERS.filter((name) => names.has(name));
    const extras = [...names].filter((name) => !ordered.includes(name)).sort((a, b) => a.localeCompare(b, "az"));
    return [{ key: "all", label: t("filter.all", "Hamısı") }, ...ordered.concat(extras).map((name) => ({ key: name, label: name }))];
  }, [partners, t]);

  const filteredBenefits = useMemo(() => {
    if (activePartner === "all") return benefits;
    return benefits.filter((benefit) => benefitPartner(benefit, t) === activePartner);
  }, [activePartner, benefits, t]);

  const unlockedBenefits = user ? filteredBenefits.filter((benefit) => benefit.available) : filteredBenefits;
  const lockedBenefits = user ? filteredBenefits.filter((benefit) => !benefit.available) : [];
  const activeVouchers = dashboard?.active_vouchers || [];
  const conciergeRows = dashboard?.concierge_status || [];
  const pendingConciergeCount = conciergeRows.filter((row) => ["new", "pending", "approved"].includes(row.status)).length;

  const redeemBenefit = async (benefit) => {
    if (!user) {
      setSelectedBenefit(benefit);
      return;
    }
    if (!benefit.available) return;
    setActionBusy(benefit.id);
    setActionMessage("");
    try {
      await api.post(`/falcon/benefits/${benefit.id}/redeem`);
      setActionMessage(t("falcon.action.created", "Sorğunuz yaradıldı. Statusu Falcon kabinetində izləyə bilərsiniz."));
      setSelectedBenefit(null);
      await load();
    } catch (err) {
      setActionMessage(getErrorMessage(err, t));
    } finally {
      setActionBusy("");
    }
  };

  return (
    <div className="bg-ivory text-graphite" data-testid="falcon-society">
      <Header />
      <main id="main">
        <section className="bg-graphite text-ivory">
          <div className="mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-9 px-6 py-12 sm:px-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(340px,0.72fr)] lg:px-10 lg:py-14">
            <div className="max-w-[720px]">
              <SmallCaps className="text-espresso">{t("falcon.hero.eyebrow", "Falcon Society")}</SmallCaps>
              <h1 className="mt-5 font-serif text-[clamp(42px,5.2vw,64px)] leading-[0.98] text-ivory">
                {t("falcon.hero.title", "Atelye imtiyazları bir üzvlükdə.")}
              </h1>
              <p className="mt-5 max-w-2xl text-[16px] leading-7 text-ivory/80">
                {t("falcon.hero.copy", "Falcon Society alışlardan qazanılan xalları, partnyor üstünlüklərini, şəxsi concierge yönləndirməsini və səviyyə əsaslı girişləri bir araya gətirir.")}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={user ? "/account?tab=loyalty" : "/auth/login?next=%2Faccount%3Ftab%3Dloyalty"}
                  className={`inline-flex min-h-[48px] items-center justify-center rounded-full border border-ivory bg-ivory px-8 py-3 text-center font-sans text-[11px] uppercase tracking-widest text-graphite transition-colors duration-300 ease-out hover:border-espresso hover:bg-espresso hover:text-white ${FOCUS}`}
                >
                  {t("falcon.cta.join", "Falcon Society-yə qoşul")}
                </Link>
                <PillCTA as={Link} to="/auth/login?next=%2Faccount%3Ftab%3Dloyalty" variant="secondary" className={`min-h-[48px] border-ivory/35 text-ivory/90 hover:bg-ivory hover:text-graphite ${FOCUS}`}>
                  {t("falcon.cta.sign_in", "Hesaba daxil ol")}
                </PillCTA>
              </div>
            </div>
            <HeroMembershipPanel
              t={t}
              user={user}
              loading={loading}
              membership={membership}
              progress={progress}
              benefitCount={benefits.filter((benefit) => benefit.available).length}
              voucherCount={activeVouchers.length}
            />
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-6 py-12 sm:px-8 lg:px-10 lg:py-14">
          <div className="flex flex-col gap-4 border-b border-graphite/10 pb-7 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <SmallCaps>{t("falcon.tiers.eyebrow", "Üzvlük səviyyələri")}</SmallCaps>
              <h2 className="mt-3 font-serif text-[clamp(34px,4.3vw,48px)] leading-none">
                {t("falcon.tiers.title", "İnkişaf yolu aydın görünsün.")}
              </h2>
            </div>
            {user && progress?.next && (
              <p className="max-w-sm text-sm leading-6 text-graphite/70">
                {t("falcon.tiers.next_hint", "Növbəti səviyyəyə {count} xal qalır.", { count: formatPoints(progress.points_to_next) })}
              </p>
            )}
          </div>
          {loading ? (
            <TierSkeleton />
          ) : (
            <TierProgression
              tiers={tiers}
              membership={membership}
              lifetimePoints={lifetimePoints}
              currentTierCode={currentTierCode}
              t={t}
            />
          )}
        </section>

        <section className="mx-auto max-w-[1280px] px-6 pb-16 sm:px-8 lg:px-10 lg:pb-20">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,0.86fr)_minmax(0,1.14fr)] lg:items-end">
            <div>
              <SmallCaps>{t("falcon.privileges.eyebrow", "Partnyor və üstünlüklər")}</SmallCaps>
              <h2 className="mt-3 font-serif text-[clamp(34px,4.3vw,48px)] leading-none">
                {t("falcon.privileges.title", "Səviyyənizə uyğun imtiyazlar.")}
              </h2>
            </div>
            <p className="text-sm leading-6 text-graphite/70">
              {t("falcon.privileges.copy", "Partnyoru seçin və səviyyənizə uyğun açılan üstünlükləri yığcam kataloqda nəzərdən keçirin.")}
            </p>
          </div>

          {user && !loading && (
            <PersonalSummary
              t={t}
              membership={membership}
              availableCount={benefits.filter((benefit) => benefit.available).length}
              voucherCount={activeVouchers.length}
              conciergeCount={pendingConciergeCount}
            />
          )}

          <PartnerFilters filters={partnerFilters} active={activePartner} onChange={setActivePartner} t={t} />
          {!user && !loading && !error && filteredBenefits.length > 0 && <GuestBenefitBanner t={t} />}

          {error ? (
            <StateBlock
              title={t("falcon.state.error_title", "Falcon məlumatı açılmadı.")}
              body={error}
              action={t("falcon.state.retry", "Yenidən yoxla")}
              onAction={load}
              icon={<RefreshCw size={18} strokeWidth={1.6} />}
            />
          ) : loading ? (
            <BenefitSkeletonGrid />
          ) : filteredBenefits.length === 0 ? (
            <StateBlock
              title={activePartner === "all" ? t("falcon.state.no_benefits", "Hazırda benefit yoxdur.") : t("falcon.state.no_filter", "Bu filter üzrə benefit tapılmadı.")}
              body={t("falcon.state.no_filter_body", "Başqa partnyor seçin və ya bir az sonra yenidən yoxlayın.")}
            />
          ) : (
            <>
              <BenefitSection
                title={user ? t("falcon.privileges.available_title", "Sizə uyğun üstünlüklər") : t("falcon.privileges.all_title", "Falcon üstünlükləri")}
                benefits={unlockedBenefits}
                user={user}
                t={t}
                actionBusy={actionBusy}
                onOpen={setSelectedBenefit}
                onRedeem={redeemBenefit}
              />
              {user && lockedBenefits.length > 0 && (
                <BenefitSection
                  title={t("falcon.privileges.locked_title", "Növbəti səviyyələrdə açılanlar")}
                  benefits={lockedBenefits}
                  user={user}
                  t={t}
                  actionBusy={actionBusy}
                  onOpen={setSelectedBenefit}
                  onRedeem={redeemBenefit}
                  muted
                />
              )}
              {user && unlockedBenefits.length === 0 && (
                <StateBlock
                  title={t("falcon.state.no_available", "Bu filter üzrə sizə açıq benefit yoxdur.")}
                  body={t("falcon.state.no_available_body", "Kilidli üstünlüklər aşağıda görünür. Xal topladıqca yeni səviyyələr açılacaq.")}
                />
              )}
            </>
          )}

          {actionMessage && (
            <div className="mt-8 border border-espresso/35 bg-stone/25 p-4 text-sm leading-6 text-graphite/75" role="status">
              {actionMessage}
            </div>
          )}
        </section>
      </main>

      <BenefitDrawer
        benefit={selectedBenefit}
        user={user}
        t={t}
        busy={actionBusy === selectedBenefit?.id}
        onClose={() => setSelectedBenefit(null)}
        onRedeem={redeemBenefit}
      />
      <Footer />
    </div>
  );
}

function HeroMembershipPanel({ t, user, loading, membership, progress, benefitCount, voucherCount }) {
  if (loading) {
    return (
      <div className="border border-graphite/12 bg-ivory p-7">
        <SkeletonLine className="h-3 w-32" />
        <SkeletonLine className="mt-8 h-10 w-52" />
        <SkeletonLine className="mt-8 h-2 w-full" />
        <div className="mt-8 grid grid-cols-2 gap-3">
          <SkeletonLine className="h-20" />
          <SkeletonLine className="h-20" />
        </div>
      </div>
    );
  }

  if (!user) {
    const rows = [
      t("falcon.hero.rule_earn", "Hər 1 AZN alış = 1 Falcon Point"),
      t("falcon.hero.rule_levels", "Xallar səviyyə və partnyor üstünlüklərini açır"),
      t("falcon.hero.rule_concierge", "Gold Gentleman və yuxarı səviyyələrdə prioritet concierge"),
    ];
    return (
      <aside className="border-l border-espresso/45 bg-ivory p-6 lg:p-7" aria-label={t("falcon.hero.panel_guest", "Falcon Points qaydaları")}>
        <SmallCaps className="text-espresso">{t("falcon.hero.panel_guest", "Falcon Points qaydaları")}</SmallCaps>
        <div className="mt-5 font-serif text-[34px] leading-none text-graphite">
          {t("falcon.hero.one_azn", "1 AZN")}
          <span className="mx-3 text-espresso">=</span>
          {t("falcon.hero.one_point", "1 xal")}
        </div>
        <div className="mt-6 grid gap-3">
          {rows.map((row) => (
            <div key={row} className="flex gap-3 border-t border-graphite/10 pt-3 text-sm leading-6 text-graphite/70">
              <Check size={16} strokeWidth={1.6} className="mt-1 shrink-0 text-espresso" />
              <span>{row}</span>
            </div>
          ))}
        </div>
      </aside>
    );
  }

  const progressPercent = Math.max(0, Math.min(100, Number(progress?.progress_percent) || 0));
  return (
    <aside className="border-l border-espresso/45 bg-ivory p-6 lg:p-7" aria-label={t("falcon.hero.panel_member", "Üzvlük paneli")}>
      <SmallCaps className="text-espresso">{t("falcon.hero.panel_member", "Üzvlük paneli")}</SmallCaps>
      <div className="mt-6 flex items-start justify-between gap-5">
        <div>
          <div className="font-serif text-[34px] leading-none text-graphite">{membership?.tier_name || t("vip.tier.gentleman_member", "Centilmen üzv")}</div>
          <p className="mt-3 text-sm leading-6 text-graphite/70">
            {progress?.next
              ? t("falcon.hero.points_to_next", "{tier} üçün {count} xal qalır.", { tier: progress.next.name, count: formatPoints(progress.points_to_next) })
              : t("falcon.hero.highest_access", "Ən yüksək açıq səviyyədəsiniz.")}
          </p>
        </div>
        <div className="text-right">
          <div className="font-serif text-[36px] leading-none text-espresso">{formatPoints(membership?.points_balance)}</div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-graphite/60">{t("falcon.points", "Falcon Points")}</div>
        </div>
      </div>
      <div className="mt-6 h-1 bg-graphite/12">
        <div className="h-full bg-espresso transition-all duration-700" style={{ width: `${progressPercent}%` }} />
      </div>
      <div className="mt-3 flex justify-between text-[10px] uppercase tracking-widest text-graphite/60">
        <span>{t("falcon.lifetime", "{count} lifetime xal", { count: formatPoints(membership?.lifetime_points) })}</span>
        <span>{progressPercent}%</span>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3">
        <HeroMetric label={t("falcon.summary.available", "Açıq benefit")} value={benefitCount} />
        <HeroMetric label={t("falcon.summary.vouchers", "Aktiv voucher")} value={voucherCount} />
      </div>
      <PillCTA as={Link} to="/account?tab=benefits" variant="secondary" className={`mt-7 min-h-[46px] w-full border-espresso/60 text-espresso hover:bg-espresso hover:text-white ${FOCUS}`}>
        {t("falcon.cta.view_my_benefits", "Üstünlüklərimə bax")}
      </PillCTA>
    </aside>
  );
}

function HeroMetric({ label, value }) {
  return (
    <div className="border border-graphite/12 bg-ivory p-4">
      <div className="font-serif text-3xl text-graphite">{formatPoints(value)}</div>
      <div className="mt-2 text-[10px] uppercase tracking-widest text-graphite/60">{label}</div>
    </div>
  );
}

function TierProgression({ tiers, membership, lifetimePoints, currentTierCode, t }) {
  if (!tiers.length) {
    return <StateBlock title={t("falcon.state.no_tiers", "Üzvlük səviyyələri tapılmadı.")} body={t("falcon.state.no_tiers_body", "Falcon səviyyələri hazır olanda burada görünəcək.")} />;
  }
  const currentIndex = Math.max(0, tiers.findIndex((tier) => tier.code === currentTierCode));
  const progressWidth = tiers.length > 1 ? `${(currentIndex / (tiers.length - 1)) * 100}%` : "0%";
  // A horizontally scrolling region has to be reachable by keyboard, or the
  // part of the table past the fold is unreadable without a mouse.
  return (
    <div className="mt-9 overflow-x-auto pb-2" tabIndex={0} role="region" aria-label={t("falcon.tiers.table", "Falcon Society səviyyələri")}>
      <div className="relative grid min-w-[820px] grid-cols-4 gap-5 lg:min-w-0">
        <div className="absolute left-[6%] right-[6%] top-5 h-px bg-graphite/14" aria-hidden="true" />
        <div className="absolute left-[6%] top-5 h-px bg-espresso transition-all duration-700" style={{ width: progressWidth }} aria-hidden="true" />
        {tiers.map((tier, index) => {
          const isCurrent = tier.code === currentTierCode;
          const unlocked = !tier.invite_only && (index <= currentIndex || lifetimePoints >= tier.min_lifetime_points);
          const pointsToGo = Math.max(0, (tier.min_lifetime_points || 0) - lifetimePoints);
          const status = isCurrent
            ? t("falcon.tier.current", "Cari səviyyəniz")
            : tier.invite_only
              ? t("falcon.tier.invite", "Yalnız dəvətlə")
              : unlocked
                ? t("falcon.tier.open", "Açıqdır")
                : t("falcon.tier.locked", "Kilidlidir");
          return (
            <article
              key={tier.code}
              data-testid="falcon-tier-card"
              className="relative pt-12"
            >
              <div className={`absolute left-0 top-0 flex h-10 w-10 items-center justify-center border bg-ivory ${isCurrent || unlocked ? "border-espresso text-espresso" : "border-graphite/25 text-graphite/70"}`}>
                {tier.invite_only && !isCurrent ? <LockKeyhole size={16} strokeWidth={1.5} /> : <span className="font-serif text-lg">{index + 1}</span>}
              </div>
              <div className="flex min-h-[34px] items-start justify-between gap-3">
                <h3 className="font-serif text-[23px] leading-none">{tierLabel(t, tier)}</h3>
                <span className={`shrink-0 border px-2 py-1 text-[9px] uppercase tracking-[0.16em] ${isCurrent ? "border-espresso text-espresso" : "border-graphite/15 text-graphite/70"}`}>
                  {status}
                </span>
              </div>
              <div className="mt-3 text-[11px] uppercase tracking-[0.14em] text-graphite/70">
                {tier.invite_only
                  ? t("falcon.tier.invite", "Yalnız dəvətlə")
                  : t("falcon.tier.min_points", "{count}+ xal", { count: formatPoints(tier.min_lifetime_points) })}
              </div>
              <BrassRule className="my-4" />
              <ul className="space-y-1.5 text-sm leading-6 text-graphite/75">
                {(tier.benefits || []).slice(0, 2).map((benefit) => <li key={benefit}>{tierBenefitLabel(t, benefit)}</li>)}
              </ul>
              {!isCurrent && !tier.invite_only && pointsToGo > 0 && (
                <div className="mt-4 text-[11px] uppercase tracking-[0.14em] text-espresso">
                  {t("falcon.tier.points_to_go", "{count} xal qalır", { count: formatPoints(pointsToGo) })}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

function PersonalSummary({ t, membership, availableCount, voucherCount, conciergeCount }) {
  const rows = [
    [t("falcon.summary.tier", "Səviyyə"), membership?.tier_name || t("vip.tier.gentleman_member", "Centilmen üzv")],
    [t("falcon.summary.points", "Aktiv xallar"), formatPoints(membership?.points_balance)],
    [t("falcon.summary.available", "Açıq benefit"), formatPoints(availableCount)],
    [t("falcon.summary.vouchers", "Aktiv voucher"), formatPoints(voucherCount)],
    [t("falcon.summary.concierge", "Gözləyən concierge"), formatPoints(conciergeCount)],
  ];
  return (
    <div className="mt-10 grid grid-cols-2 gap-px border border-graphite/10 bg-graphite/10 md:grid-cols-5" data-testid="falcon-personal-summary">
      {rows.map(([label, value]) => (
        <div key={label} className="bg-ivory p-4">
          <div className="text-[10px] uppercase tracking-widest text-graphite/70">{label}</div>
          <div className="mt-2 truncate font-serif text-[24px] leading-none">{value}</div>
        </div>
      ))}
    </div>
  );
}

function PartnerFilters({ filters, active, onChange, t }) {
  return (
    <div className="mt-10 border-y border-graphite/10 py-4">
      <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label={t("falcon.filters.label", "Falcon partnyor filterləri")}>
        {filters.map((filter) => {
          const selected = active === filter.key;
          return (
            <button
              key={filter.key}
              type="button"
              data-testid="falcon-partner-filter"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(filter.key)}
              className={`min-h-[42px] shrink-0 border px-5 text-[11px] uppercase tracking-widest transition ${FOCUS} ${
                selected ? "border-graphite bg-graphite text-ivory" : "border-graphite/15 text-graphite/70 hover:border-espresso hover:text-espresso"
              }`}
            >
              {filter.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GuestBenefitBanner({ t }) {
  return (
    <div className="mt-7 grid gap-4 border-y border-espresso/25 bg-stone/20 px-4 py-5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:px-6" data-testid="falcon-guest-benefit-banner">
      <div>
        <h3 className="font-serif text-[26px] leading-none">{t("falcon.guest_banner.title", "Üstünlüklər hesabınızla açılır.")}</h3>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-graphite/75">
          {t("falcon.guest_banner.body", "Benefit detalları açıqdır; uyğun səviyyə, voucher və concierge sorğuları üçün hesabınıza daxil olun.")}
        </p>
      </div>
      <Link
        to="/auth/login?next=/falcon-society"
        className={`inline-flex min-h-[44px] items-center justify-center border border-graphite px-5 text-[11px] uppercase tracking-widest text-graphite transition hover:bg-graphite hover:text-ivory ${FOCUS}`}
      >
        {t("falcon.guest_banner.cta", "Hesaba daxil ol")}
      </Link>
    </div>
  );
}

function BenefitSection({ title, benefits, user, t, actionBusy, onOpen, onRedeem, muted = false }) {
  if (!benefits.length) return null;
  return (
    <section className="mt-10">
      <div className="mb-5 flex items-center justify-between gap-4">
        <h3 className="font-serif text-[32px] leading-none">{title}</h3>
        <span className="text-[10px] uppercase tracking-widest text-graphite/70">{formatPoints(benefits.length)}</span>
      </div>
      <div className={`divide-y divide-graphite/10 border-y border-graphite/10 ${muted ? "opacity-[0.86]" : ""}`}>
        {benefits.map((benefit) => (
          <BenefitCard
            key={benefit.id}
            benefit={benefit}
            user={user}
            t={t}
            busy={actionBusy === benefit.id}
            onOpen={onOpen}
            onRedeem={onRedeem}
          />
        ))}
      </div>
    </section>
  );
}

function BenefitCard({ benefit, user, t, busy, onOpen, onRedeem, withAction = true }) {
  const partner = benefitPartner(benefit, t);
  const type = benefitTypeLabel(benefit, t);
  const available = Boolean(user && benefit.available);
  const actionLabel = !user
    ? t("falcon.cta.detail", "Ətraflı bax")
    : available
      ? ctaForBenefit(benefit, t)
      : t("falcon.cta.unlocks_at_level", "Bu səviyyədə açılır");
  const status = !user
    ? null
    : benefit.available
      ? t("falcon.status.available", "Uyğundur")
      : t("falcon.status.locked", "Kilidlidir");
  const actionOpensDetail = !user;
  return (
    <article data-testid="falcon-benefit-card" className="grid gap-4 bg-ivory py-5 transition hover:bg-stone/20 lg:grid-cols-[170px_minmax(0,1fr)_210px_auto] lg:items-center">
      <div className="flex items-center justify-between gap-3 lg:block">
        <div className="text-[11px] uppercase tracking-[0.14em] text-espresso">{partner}</div>
        {status && (
          <span className={`mt-0 inline-flex shrink-0 border px-2 py-1 text-[9px] uppercase tracking-widest lg:mt-3 ${available ? "border-espresso text-espresso" : "border-graphite/15 text-graphite/70"}`}>
            {status}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <h4 className="font-serif text-[24px] leading-[1.05]">{benefit.title}</h4>
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-graphite/75">
          {benefit.description || t("falcon.benefit.no_description", "Ətraflı məlumat benefit detalında göstərilir.")}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px] uppercase tracking-[0.12em] text-graphite/70 lg:block lg:space-y-2 lg:text-right">
        <div>{benefit.tier_required_name || tierName(benefit.tier_required)}</div>
        <div>{type}</div>
      </div>
      <div className="flex justify-start lg:justify-end">
        {withAction && (
          <button
            type="button"
            disabled={Boolean(user && (!available || busy))}
            onClick={() => (actionOpensDetail ? onOpen(benefit) : onRedeem(benefit))}
            className={`inline-flex min-h-[42px] min-w-[140px] items-center justify-center border px-4 text-[11px] uppercase tracking-widest transition ${FOCUS} ${
              available ? "border-graphite bg-graphite text-ivory hover:border-espresso hover:bg-espresso" : actionOpensDetail ? "border-graphite/18 text-graphite/70 hover:border-espresso hover:text-espresso" : "cursor-not-allowed border-graphite/12 text-graphite/70"
            }`}
          >
            {busy ? t("falcon.cta.processing", "Göndərilir") : actionLabel}
          </button>
        )}
        {user && (
          <button
          type="button"
          onClick={() => onOpen(benefit)}
          className={`ml-3 inline-flex min-h-[42px] w-11 items-center justify-center border border-graphite/10 text-graphite/70 transition hover:border-espresso hover:text-espresso ${FOCUS}`}
          aria-label={t("falcon.cta.detail", "Ətraflı bax")}
        >
            <ArrowUpRight size={14} strokeWidth={1.5} />
          </button>
        )}
      </div>
    </article>
  );
}

function BenefitDrawer({ benefit, user, t, busy, onClose, onRedeem }) {
  if (!benefit) return null;
  const available = Boolean(user && benefit.available);
  return (
    <AtelierModal
      open={Boolean(benefit)}
      onClose={onClose}
      title={t("falcon.drawer.eyebrow", "Benefit detalı")}
      subtitle={benefit.title}
      testid="falcon-benefit-drawer"
      width="620px"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-px border border-graphite/10 bg-graphite/10">
          <DetailMetric label={t("falcon.card.partner", "Partnyor")} value={benefitPartner(benefit, t)} />
          <DetailMetric label={t("falcon.card.type", "Tip")} value={benefitTypeLabel(benefit, t)} />
          <DetailMetric label={t("falcon.card.tier", "Səviyyə")} value={benefit.tier_required_name || tierName(benefit.tier_required)} />
          <DetailMetric label={t("falcon.card.points", "Xal")} value={benefit.points_required ? `${formatPoints(benefit.points_required)} xal` : t("falcon.card.no_points", "Xal tələb etmir")} />
        </div>
        <p className="text-sm leading-7 text-graphite/75">
          {benefit.description || t("falcon.benefit.no_description", "Ətraflı məlumat benefit detalında göstərilir.")}
        </p>
        {benefit.terms && (
          <div className="border border-espresso/25 bg-stone/20 p-4 text-sm leading-7 text-graphite/75">
            <SmallCaps>{t("falcon.drawer.terms", "Şərtlər")}</SmallCaps>
            <p className="mt-3">{benefit.terms}</p>
          </div>
        )}
        <div className="flex flex-col gap-3 sm:flex-row">
          {available ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onRedeem(benefit)}
              className={`min-h-[46px] flex-1 border border-graphite bg-graphite px-5 text-[11px] uppercase tracking-widest text-ivory transition hover:border-espresso hover:bg-espresso ${FOCUS}`}
            >
              {busy ? t("falcon.cta.processing", "Göndərilir") : ctaForBenefit(benefit, t)}
            </button>
          ) : user ? (
            <div className="flex min-h-[46px] flex-1 items-center justify-center border border-graphite/12 px-5 text-[11px] uppercase tracking-widest text-graphite/70">
              {t("falcon.cta.unlocks_at_level", "Bu səviyyədə açılır")}
            </div>
          ) : (
            <Link
              to="/auth/login?next=/falcon-society"
              onClick={onClose}
              className={`inline-flex min-h-[46px] flex-1 items-center justify-center border border-graphite bg-graphite px-5 text-[11px] uppercase tracking-widest text-ivory transition hover:border-espresso hover:bg-espresso ${FOCUS}`}
            >
              {t("falcon.cta.sign_in", "Hesaba daxil ol")}
            </Link>
          )}
          <button type="button" onClick={onClose} className={`min-h-[46px] border border-graphite/15 px-5 text-[11px] uppercase tracking-widest text-graphite/70 transition hover:border-espresso hover:text-espresso ${FOCUS}`}>
            {t("common.close", "Bağla")}
          </button>
        </div>
      </div>
    </AtelierModal>
  );
}

function DetailMetric({ label, value }) {
  return (
    <div className="bg-ivory p-4">
      <div className="text-[10px] uppercase tracking-widest text-graphite/70">{label}</div>
      <div className="mt-2 font-serif text-xl leading-tight">{value}</div>
    </div>
  );
}

function StateBlock({ title, body, action, onAction, icon }) {
  return (
    <div className="mt-10 flex min-h-[220px] flex-col items-center justify-center border border-graphite/12 bg-ivory p-8 text-center">
      {icon && <div className="mb-5 flex h-10 w-10 items-center justify-center border border-espresso/35 text-espresso">{icon}</div>}
      <h3 className="font-serif text-[34px] leading-none">{title}</h3>
      {body && <p className="mt-4 max-w-md text-sm leading-6 text-graphite/70">{body}</p>}
      {action && (
        <button type="button" onClick={onAction} className={`mt-6 border border-graphite px-5 py-3 text-[11px] uppercase tracking-widest transition hover:bg-graphite hover:text-ivory ${FOCUS}`}>
          {action}
        </button>
      )}
    </div>
  );
}

function SkeletonLine({ className = "", dark = false }) {
  return <div className={`animate-pulse ${dark ? "bg-ivory/12" : "bg-graphite/8"} ${className}`} />;
}

function TierSkeleton() {
  return (
    <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-4">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="border border-graphite/10 p-5">
          <SkeletonLine className="h-12 w-12" />
          <SkeletonLine className="mt-5 h-8 w-3/4" />
          <SkeletonLine className="mt-5 h-3 w-full" />
          <SkeletonLine className="mt-3 h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

function BenefitSkeletonGrid() {
  return (
    <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
      {[0, 1, 2, 3, 4, 5].map((item) => (
        <div key={item} className="min-h-[300px] border border-graphite/10 p-5">
          <SkeletonLine className="h-3 w-32" />
          <SkeletonLine className="mt-6 h-10 w-4/5" />
          <SkeletonLine className="mt-6 h-3 w-full" />
          <SkeletonLine className="mt-3 h-3 w-5/6" />
          <SkeletonLine className="mt-10 h-11 w-full" />
        </div>
      ))}
    </div>
  );
}

export function FalconDashboard() {
  const { user, ready } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [conciergeMessage, setConciergeMessage] = useState("");

  const load = async () => {
    const r = await api.get("/falcon/dashboard");
    setData(r.data);
  };

  useEffect(() => { if (user) load(); }, [user]);
  if (!ready) return <div className="bg-ivory min-h-screen"><Header /></div>;
  if (!user) return <Navigate to="/auth/login?next=%2Faccount%3Ftab%3Dloyalty" replace />;
  if (!data) return <div className="bg-ivory min-h-screen"><Header /></div>;

  const membership = data.membership || {};
  const progress = data.progress || {};
  const requestConcierge = async () => {
    await api.post("/falcon/concierge/request", { request_type: "general", message: conciergeMessage || "Falcon Society concierge request" });
    analytics.lead("falcon_concierge");
    setConciergeMessage("");
    setMsg(t("falcon.action.created", "Sorğunuz yaradıldı. Statusu Falcon kabinetində izləyə bilərsiniz."));
    load();
  };

  return (
    <div className="bg-ivory text-graphite" data-testid="falcon-dashboard">
      <Header />
      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 py-16">
        <SmallCaps>{t("falcon.hero.eyebrow", "Falcon Society")}</SmallCaps>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 bg-graphite text-ivory border border-espresso/50 p-8 lg:p-10">
            <div className="flex justify-between gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-espresso">{t("falcon.dashboard.card", "Üzvlük kartı")}</div>
                <h1 className="font-serif italic text-5xl mt-4">{membership.tier_name || t("falcon.hero.eyebrow", "Falcon Society")}</h1>
                <div className="mt-3 text-ivory/68 uppercase tracking-widest text-xs">
                  {membership.falcon_member ? t("falcon.status.active_member", "Aktiv üzv") : t("falcon.status.join_after_purchase", "Alışdan sonra qoşul")}
                </div>
              </div>
              <div className="text-right">
                <div className="font-serif text-4xl text-espresso">{membership.points_balance || 0}</div>
                <div className="text-[10px] uppercase tracking-widest text-ivory/58">{t("falcon.points_short", "xal")}</div>
              </div>
            </div>
            <div className="mt-10 h-1 bg-ivory/15 overflow-hidden">
              <div className="h-full bg-espresso" style={{ width: `${progress.progress_percent || 0}%` }} />
            </div>
            <div className="mt-3 flex justify-between text-[11px] uppercase tracking-widest text-ivory/58">
              <span>{t("falcon.lifetime", "{count} lifetime xal", { count: membership.lifetime_points || 0 })}</span>
              <span>{progress.next ? t("falcon.hero.points_to_next", "{tier} üçün {count} xal qalır.", { tier: progress.next.name, count: progress.points_to_next }) : t("falcon.hero.highest_access", "Ən yüksək açıq səviyyədəsiniz.")}</span>
            </div>
          </div>
          <div className="lg:col-span-5 border border-graphite/10 bg-ivory/40 p-8">
            <SmallCaps>{t("falcon.dashboard.concierge", "Falcon Concierge")}</SmallCaps>
            <textarea
              value={conciergeMessage}
              onChange={(e) => setConciergeMessage(e.target.value)}
              className={`mt-5 w-full min-h-[120px] bg-transparent border border-graphite/20 p-4 focus:outline-none focus:border-espresso ${FOCUS}`}
              placeholder={t("falcon.dashboard.concierge_placeholder", "Stil, rezervasiya, səyahət, tədbir və ya şəxsi sorğu")}
            />
            <PillCTA variant="primary" className={`mt-4 ${FOCUS}`} onClick={requestConcierge}>{t("cta.concierge", "Concierge ilə əlaqə")}</PillCTA>
            {msg && <div className="mt-4 text-sm text-espresso">{msg}</div>}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 pb-20 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Panel title={t("falcon.privileges.available_title", "Sizə uyğun üstünlüklər")}>
          {(data.available_benefits || []).map((b) => <BenefitCard key={b.id} benefit={b} user={user} t={t} onOpen={() => {}} onRedeem={async () => { await api.post(`/falcon/benefits/${b.id}/redeem`); load(); }} />)}
        </Panel>
        <Panel title={t("falcon.summary.vouchers", "Aktiv voucher")}>
          {(data.active_vouchers || []).map((v) => (
            <div key={v.id} className="border-b border-graphite/10 py-4">
              <div className="font-serif text-xl">{v.benefit?.title}</div>
              <div className="text-[11px] uppercase tracking-widest text-graphite/70 mt-1">{v.status} · {v.voucher_code}</div>
            </div>
          ))}
          {(data.active_vouchers || []).length === 0 && <EmptyLine>{t("falcon.empty.no_vouchers", "Aktiv voucher yoxdur.")}</EmptyLine>}
        </Panel>
        <Panel title={t("falcon.dashboard.points_history", "Xal tarixçəsi")}>
          {(data.recent_transactions || []).map((tx) => (
            <div key={tx.id} className="flex justify-between border-b border-graphite/10 py-4">
              <div><div className="font-serif text-lg">{tx.description}</div><div className="text-xs text-graphite/70">{tx.source}</div></div>
              <div className="font-serif text-xl text-espresso">{tx.points > 0 ? "+" : ""}{tx.points}</div>
            </div>
          ))}
          {(data.recent_transactions || []).length === 0 && <EmptyLine>{t("falcon.empty.no_points", "Xal tarixçəsi yoxdur.")}</EmptyLine>}
        </Panel>
      </section>
      <Footer />
    </div>
  );
}

export function FalconBenefitsPage() {
  const { user, ready } = useAuth();
  const { t } = useI18n();
  const [benefits, setBenefits] = useState([]);
  const [filter, setFilter] = useState("");
  useEffect(() => { api.get("/falcon/benefits", { params: filter ? { benefit_type: filter } : {} }).then((r) => setBenefits(r.data || [])); }, [filter]);
  if (!ready) return <div className="bg-ivory min-h-screen"><Header /></div>;
  if (!user) return <Navigate to="/auth/login?next=%2Faccount%3Ftab%3Dbenefits" replace />;
  return (
    <Shell title={t("falcon.privileges.all_title", "Falcon üstünlükləri")} eyebrow={t("falcon.drawer.eyebrow", "Benefit kataloqu")}>
      <div className="flex flex-wrap gap-2 mb-10">
        {["", "priority_access", "consultation", "voucher", "invitation", "concierge"].map((x) => (
          <button key={x || "all"} onClick={() => setFilter(x)} className={`px-5 py-2 border text-[11px] uppercase tracking-widest ${FOCUS} ${filter === x ? "bg-graphite text-ivory" : "border-graphite/20"}`}>
            {x || t("filter.all", "Hamısı")}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {benefits.map((b) => <BenefitCard key={b.id} benefit={b} user={user} t={t} onOpen={() => {}} onRedeem={async () => { await api.post(`/falcon/benefits/${b.id}/redeem`); }} />)}
      </div>
    </Shell>
  );
}

export function FalconPartnerDetail() {
  const { slug } = useParams();
  const { user, ready } = useAuth();
  const { t } = useI18n();
  const [partner, setPartner] = useState(null);
  useEffect(() => { api.get(`/falcon/partners/${slug}`).then((r) => setPartner(r.data)); }, [slug]);
  if (!ready || !partner) return <div className="bg-ivory min-h-screen"><Header /></div>;
  if (!user) return <Navigate to="/auth/login?next=%2Faccount%3Ftab%3Dbenefits" replace />;
  return (
    <Shell title={partner.name} eyebrow={t("falcon.card.partner", "Falcon partnyoru")}>
      <p className="max-w-3xl text-graphite/75 font-light">{partner.full_description || partner.short_description}</p>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {(partner.benefits || []).map((b) => <BenefitCard key={b.id} benefit={b} user={user} t={t} onOpen={() => {}} onRedeem={async () => { await api.post(`/falcon/benefits/${b.id}/redeem`); }} />)}
      </div>
    </Shell>
  );
}

function Shell({ eyebrow, title, children }) {
  return (
    <div className="bg-ivory text-graphite">
      <Header />
      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 py-16">
        <SmallCaps>{eyebrow}</SmallCaps>
        <h1 className="font-serif italic text-5xl lg:text-6xl mt-4">{title}</h1>
        <div className="mt-12">{children}</div>
      </section>
      <Footer />
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div>
      <SmallCaps>{title}</SmallCaps>
      <div className="mt-5 space-y-4">{children}</div>
    </div>
  );
}

function EmptyLine({ children }) {
  return <div className="font-serif italic text-graphite/70 py-4">{children}</div>;
}
